import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark bg-background text-foreground grid place-items-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center"><HardHat className="h-5 w-5 text-primary-foreground" /></div>
          <div className="font-extrabold tracking-tight text-lg">SmartBuild <span className="text-primary">AI</span></div>
        </div>

        {!token ? (
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight mb-2">Invalid link</h1>
            <p className="text-sm text-muted-foreground mb-6">This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-primary font-semibold hover:underline text-sm">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="h-12 w-12 rounded-full bg-emerald-500/15 grid place-items-center mx-auto mb-4"><CheckCircle2 className="h-6 w-6 text-emerald-500" /></div>
            <h1 className="text-2xl font-bold tracking-tight mb-2">Password updated</h1>
            <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h1 className="text-2xl font-bold tracking-tight mb-1">Set a new password</h1>
            <p className="text-sm text-muted-foreground mb-6">Choose a new password for your account.</p>
            <label className="block text-sm font-semibold mb-1.5">New password</label>
            <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="At least 6 characters" />
            <label className="block text-sm font-semibold mb-1.5">Confirm password</label>
            <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-6 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="Re-enter password" />
            <button data-testid="reset-submit-btn" disabled={loading}
              className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
            <p className="text-sm text-muted-foreground mt-5 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-primary font-semibold hover:underline"><ArrowLeft className="h-4 w-4" />Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
